import React from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Card, CardContent } from '@/shared/ui/Card'
import { Button } from '@/shared/ui'
import { getLogo } from '@/modules/branding/brandingService'
import { Modal } from '@/shared/ui/Modal'
import { useToast } from '@/shared/ui/Toast'
import CheckoutPersonalForm from '@/modules/checkout/components/CheckoutPersonalForm'
import { captureReferralFromLocation, getReferralCode } from '@/modules/affiliates/services/referralService'
import { setSelectedPlan, getSelectedPlan } from '@/modules/checkout/utils/selection'
import { buildCadastroWebhookPayload, sendCadastroWebhook, shouldSendCadastroWebhook } from '@/modules/auth/services/cadastroWebhookService'
import { isMasterByEmail } from '@/shared/utils/role'

export default function SignupPage() {
  const { add: toast } = useToast()
  const nav = useNavigate()
  const [params] = useSearchParams()
  const [logoUrl, setLogoUrl] = React.useState<string | null>(null)
  const [emailExistsModal, setEmailExistsModal] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)
  const [loading, setLoading] = React.useState(false)
  const [details, setDetails] = React.useState({ name: '', email: '', password: '', confirmPassword: '', phone: '', referralCode: '' })
  const [refLocked, setRefLocked] = React.useState(false)

  React.useEffect(() => {
    ;(async () => {
      try {
        const isDark = document.documentElement.classList.contains('dark') || window.matchMedia?.('(prefers-color-scheme: dark)')?.matches
        const url = await getLogo('home' as any, !isDark)
        setLogoUrl(url)
      } catch {}
    })()
  }, [])

  React.useEffect(() => {
    const plan = params.get('plan'); const interval = params.get('interval')
    const trialFlag = (params.get('trial') || '').trim().toLowerCase()
    if (plan) setSelectedPlan({ plan, interval: (interval === 'year' ? 'year' : 'month') })
    else if (trialFlag === '1' || trialFlag === '7d') setSelectedPlan({ plan: 'trial_7d', interval: 'month' })
  }, [])

  // Captura ?ref= e preenche referralCode; fallback para cache (30 dias)
  React.useEffect(() => {
    try { captureReferralFromLocation(window.location) } catch {}
    const qRef = (params.get('ref') || params.get('referral') || '').trim()
    if (qRef) { setDetails((d) => ({ ...d, referralCode: qRef })); setRefLocked(true) }
    else {
      try { const cached = getReferralCode(); if (cached) { setDetails((d) => ({ ...d, referralCode: d.referralCode || cached })); setRefLocked(true) } } catch {}
    }
  }, [])

  async function onSubmit() {
    setError(null)
    try {
      setLoading(true)
      const email = (details.email || '').trim().toLowerCase()
      if (!/.+@.+\..+/.test(email)) throw new Error('Informe um e-mail valido')
      if ((details.password || '').length < 8) throw new Error('A senha deve ter pelo menos 8 caracteres')
      if (details.password !== details.confirmPassword) throw new Error('As senhas nao conferem')
      const phone = (details.phone || '').trim()
      if (!phone) throw new Error('Informe um telefone valido')
      if (isMasterByEmail(email)) { setEmailExistsModal(true); return }
      const sb = (await import('@/lib/supabase')).supabase
      try {
        const { data } = await sb.functions.invoke('check-email-availability', { body: { email } })
        const resp = Array.isArray(data) ? (data as any)[0] : (data as any)
        if (resp?.available === false) { setEmailExistsModal(true); return }
      } catch {}
      try {
        const { data } = await sb.functions.invoke('check-phone-availability', { body: { phone } })
        const resp = Array.isArray(data) ? (data as any)[0] : (data as any)
        if (resp?.available === false) {
          throw new Error('Este numero de telefone ja esta vinculado a outra conta. Faca login ou recupere seu acesso.')
        }
      } catch (e: any) {
        if (e?.message) throw e
      }
      const su = await sb.auth.signUp({ email, password: details.password, options: { data: { role: 'member', full_name: details.name || undefined, phone: phone || undefined } } });
      if (su.error) {
        const msg = (su.error?.message || '').toLowerCase()
        if (msg.includes('already') || msg.includes('registered') || msg.includes('exists')) { setEmailExistsModal(true); return }
        throw su.error
      }

      if (shouldSendCadastroWebhook(params)) {
        const payload = buildCadastroWebhookPayload(details.name, phone)
        if (payload.nome_completo && payload.numero_telefone) {
          try {
            const { error: webhookError } = await sendCadastroWebhook(payload)
            if (webhookError) console.warn('cadastro-webhook failed')
          } catch {
            console.warn('cadastro-webhook failed')
          }
        }
      }

      // Garante cliente + confirmaÃ§Ã£o e cria sessÃ£o antes de ir ao checkout
      try { await sb.functions.invoke('admin-ensure-customer', { body: { email, name: details.name || undefined, phone: phone || undefined } }) } catch {}
      try { await sb.auth.signInWithPassword({ email, password: details.password }) } catch {}

      const sel = getSelectedPlan()
      if (!sel) {
        toast({ title: 'Cadastro', description: 'Cadastro concluido. Selecione um plano para continuar.', soft: true })
      }
      // Monta querystring para o checkout (freemium usa o plano trial_7d sem flag extra)
      const qp: string[] = []
      const trialFlag = (params.get('trial') || '').trim().toLowerCase()
      const isFreemiumSelection = !!sel && (sel.plan.toLowerCase() === 'trial_7d' || trialFlag === '1' || trialFlag === '7d')
      if (sel) {
        qp.push(`plan=${encodeURIComponent(sel.plan)}`)
        qp.push(`interval=${encodeURIComponent(sel.interval)}`)
      } else if (isFreemiumSelection) {
        qp.push('plan=trial_7d')
        qp.push('interval=month')
      }
      const coupon = (details as any).couponCode ? String((details as any).couponCode).trim() : ''
      if (coupon) qp.push(`coupon=${encodeURIComponent(coupon)}`)
      const ref = (details as any).referralCode ? String((details as any).referralCode).trim() : ''
      if (ref) qp.push(`ref=${encodeURIComponent(ref)}`)
      const target = `/checkout${qp.length ? ('?' + qp.join('&')) : ''}`
      nav(target, { replace: true })
    } catch (e:any) { setError(e?.message || 'Falha no cadastro') }
    finally { setLoading(false) }
  }

  return (
    <div className="mx-auto w-full max-w-[900px] pb-12">
      <div className="mt-[50px] mb-[50px] flex items-center justify-center">{!!logoUrl && (<img src={logoUrl} alt="logo" className="h-14 w-auto" />)}</div>
      <div className="mb-6 text-center">
        <h1 className="text-2xl font-semibold">Criar sua conta</h1>
        <p className="text-sm text-foreground/70">Preencha seus dados para continuar para o pagamento.</p>
      </div>
      <Card>
        <CardContent className="pt-6 space-y-4">
          {error && (<div className="rounded-md border border-destructive/30 bg-destructive/10 p-2 text-sm text-destructive-foreground">{error}</div>)}
          <CheckoutPersonalForm value={{ ...details }} onChange={(p)=> setDetails({ ...details, ...p })} disabledReferral={refLocked} hideCoupon />
          <div className="flex justify-end"><Button disabled={loading} onClick={onSubmit}>{loading ? 'Cadastrando...' : 'Cadastrar e ir ao pagamento'}</Button></div>
        </CardContent>
      </Card>

      <Modal open={emailExistsModal} onOpenChange={setEmailExistsModal}>
        <div className="space-y-3">
          <h2 className="text-lg font-semibold">E-mail ja cadastrado</h2>
          <p className="text-sm text-foreground/70">Este e-mail ja possui cadastro. FaÃ§a login para continuar.</p>
          <div className="flex justify-end gap-2">
            <Button onClick={()=> nav('/login', { replace: true })}>Ir para login</Button>
          </div>
        </div>
      </Modal>
    </div>
  )
}
